import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import RoleBadge from './RoleBadge';

interface Owner {
  id: string;
  display_name: string;
  avatar_url?: string;
  role?: string;
}

interface UserBadgeProps {
  owner: Owner | null | undefined;
  size?: 'sm' | 'md';
  showRole?: boolean;
  clickable?: boolean;
}

const sizeConfig = {
  sm: { avatar: 'h-5 w-5 text-[9px]', name: 'text-xs', gap: 'gap-1.5' },
  md: { avatar: 'h-7 w-7 text-xs', name: 'text-sm', gap: 'gap-2' },
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

const UserBadge: React.FC<UserBadgeProps> = ({
  owner,
  size = 'sm',
  showRole = false,
  clickable = false,
}) => {
  const { darkMode } = useTheme();
  const s = sizeConfig[size];

  // System badge (owner is null/undefined)
  if (!owner) {
    return (
      <div className={`inline-flex items-center ${s.gap}`}>
        <div className={`${s.avatar} rounded-full flex items-center justify-center ${
          darkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'
        }`}>
          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <span className={`${s.name} ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>System</span>
      </div>
    );
  }

  const content = (
    <div className={`inline-flex items-center ${s.gap}`}>
      {/* Avatar */}
      {owner.avatar_url ? (
        <img src={owner.avatar_url} alt={owner.display_name} className={`${s.avatar} rounded-full object-cover`} />
      ) : (
        <div className={`${s.avatar} rounded-full flex items-center justify-center font-medium ${
          darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
        }`}>
          {getInitials(owner.display_name)}
        </div>
      )}

      {/* Name */}
      <span className={`${s.name} ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {owner.display_name}
      </span>

      {/* Role pill */}
      {showRole && owner.role && <RoleBadge role={owner.role} size="sm" />}
    </div>
  );

  if (clickable && owner.id) {
    return (
      <a
        href={`/dashboards/user/${owner.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:opacity-80 transition-opacity"
      >
        {content}
      </a>
    );
  }

  return content;
};

export default UserBadge;
